import React, { useRef } from "react";
import { ArrowDown, ArrowLeft, ArrowRight, ArrowUp, Lock } from "lucide-react";
import type { FloorplanLinkArrow, FloorplanSceneObject } from "@mvk/shared";
import { FLOORPLAN_KIND_STYLE } from "@mvk/shared";
import { PinCardLabels } from "./PinCardLabels";
import { fitPinLabel, normRot } from "./pinLabelFit";

function hexAccent(raw: string | undefined): string | null {
  const s = (raw ?? "").trim();
  return /^#[0-9a-fA-F]{6}$/.test(s) ? s : null;
}

function hexAlpha(hex: string, alphaHex: string): string {
  return `${hex}${alphaHex}`;
}

function LinkArrowIcon({
  dir,
  size,
  color,
}: {
  dir: FloorplanLinkArrow;
  size: number;
  color: string;
}) {
  const props = { size, color, strokeWidth: 2.4, className: "flex-shrink-0" as const };
  if (dir === "w") return <ArrowLeft {...props} />;
  if (dir === "n") return <ArrowUp {...props} />;
  if (dir === "s") return <ArrowDown {...props} />;
  return <ArrowRight {...props} />;
}

type ResizeCorner = "se" | "sw" | "ne" | "nw";
type BoardRotation = 0 | 90 | 180 | 270;

type Props = {
  objects: FloorplanSceneObject[];
  selectedId: string | null;
  editable?: boolean;
  interactive?: boolean;
  stampPassthrough?: boolean;
  viewZoom?: number;
  labelScale?: number;
  boardWidth?: number;
  boardHeight?: number;
  boardRotation?: BoardRotation;
  labelsUpright?: boolean;
  /** Global extra rotation for all labels (scene.pinLabelRotationDeg). */
  pinLabelRotationDeg?: BoardRotation;
  /** Selected marker accent (event primary). Default indigo. */
  selectedColor?: string;
  /** Per-object accent override (e.g. event_link → destination theme). */
  accentById?: Record<string, string>;
  onSelect?: (id: string | null) => void;
  onMoveStart?: (id: string, clientX: number, clientY: number) => void;
  onResizeStart?: (id: string, corner: ResizeCorner, clientX: number, clientY: number) => void;
};

function rotateEdge(
  edge: "n" | "s" | "e" | "w",
  rotationDeg: BoardRotation,
): "n" | "s" | "e" | "w" {
  const order = ["n", "e", "s", "w"] as const;
  const i = order.indexOf(edge);
  const t = (rotationDeg / 90) % 4;
  return order[(i + t) % 4]!;
}

function cursorForCorner(corner: ResizeCorner, rotationDeg: BoardRotation): string {
  const a = rotateEdge(corner[0] as "n" | "s" | "e" | "w", rotationDeg);
  const b = rotateEdge(corner[1] as "n" | "s" | "e" | "w", rotationDeg);
  const set = new Set([a, b]);
  if ((set.has("n") && set.has("w")) || (set.has("s") && set.has("e"))) {
    return "nwse-resize";
  }
  return "nesw-resize";
}

function exhibitorNames(label: string | undefined): string[] {
  return String(label ?? "")
    .split(/\s*[·|\n;]\s*/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function kindLabel(kind: FloorplanSceneObject["kind"]): string {
  switch (kind) {
    case "entrance":
      return "Вход";
    case "exit":
      return "Выход";
    case "toilet":
      return "Туалет";
    case "stairs":
      return "Лестница";
    case "info":
      return "Инфо";
    case "service":
      return "Сервис";
    case "zone":
      return "Зона";
    case "booth":
      return "Стенд";
    case "event_link":
      return "Переход";
    default:
      return "Объект";
  }
}

/**
 * Editable scheme markers. Resize handles stay outside the box.
 */
export function FloorplanOverlayMarkers({
  objects,
  selectedId,
  editable = false,
  interactive = false,
  stampPassthrough = false,
  viewZoom = 1,
  labelScale = 1,
  boardWidth = 1000,
  boardHeight = 700,
  boardRotation = 0,
  labelsUpright = true,
  pinLabelRotationDeg = 0,
  selectedColor = "#4f46e5",
  accentById,
  onSelect,
  onMoveStart,
  onResizeStart,
}: Props) {
  const z = Math.max(0.05, viewZoom);
  const inv = 1 / z;
  const borderPx = Math.max(1, Number((1.4 * inv).toFixed(3)));
  const screenAlign = labelsUpright && boardRotation ? normRot(-boardRotation) : 0;
  const globalLabelTwist = normRot(pinLabelRotationDeg);
  const sel = /^#[0-9a-fA-F]{6}$/.test(selectedColor.trim())
    ? selectedColor.trim()
    : "#4f46e5";
  const selRgb = sel
    .slice(1)
    .match(/.{2}/g)!
    .map((h) => parseInt(h, 16));
  const selGlow = `0 0 0 2px rgba(${selRgb[0]},${selRgb[1]},${selRgb[2]},0.4), 0 2px 10px rgba(${selRgb[0]},${selRgb[1]},${selRgb[2]},0.35)`;
  const baseScale = Math.min(2.2, Math.max(0.45, labelScale));
  const handlePx = Math.max(12, Math.min(18, 14 * inv));
  const canEdit = editable && !stampPassthrough;
  const canTap = !stampPassthrough && (canEdit || interactive);
  const tapStartRef = useRef<{ id: string; x: number; y: number } | null>(null);

  return (
    <>
      {objects.map((o) => {
        const kindStyle = FLOORPLAN_KIND_STYLE[o.kind] ?? FLOORPLAN_KIND_STYLE.other;
        const selected = !stampPassthrough && selectedId === o.id;
        const isBooth = o.kind === "booth";
        const isEventLink = o.kind === "event_link";
        const locked = o.locked === true;
        const names = isBooth ? exhibitorNames(o.label) : [];
        const title = isBooth
          ? [o.code, ...names].filter(Boolean).join(" · ")
          : (o.label || o.code || o.kind).trim();
        const dragOk = canEdit && !locked;
        const objScale =
          typeof o.fontScale === "number" && o.fontScale > 0
            ? Math.min(2.5, Math.max(0.5, o.fontScale))
            : 1;
        const perLabelRot = normRot(o.labelRot ?? 0);
        const nameRot = normRot(screenAlign + globalLabelTwist + perLabelRot);
        const label = (o.label || o.code || kindLabel(o.kind)).trim();
        const codeText = (o.code || "").trim();
        const accent = hexAccent(accentById?.[o.id]) ?? hexAccent(o.linkAccent);
        const stroke = accent ?? kindStyle.stroke;
        const fill = accent ? hexAlpha(accent, "22") : kindStyle.fill;
        const textCol = accent ?? kindStyle.text;
        const selectedFill = accent ? hexAlpha(accent, "55") : `${sel}55`;
        const selectedStroke = accent ?? sel;

        return (
          <div
            key={o.id}
            data-map-marker
            {...(canTap ? { "data-map-no-pan": "" } : {})}
            onPointerDown={(e) => {
              if (!canTap) return;
              if ((e.target as HTMLElement).closest("[data-resize-handle]")) return;
              e.stopPropagation();
              // Visitor: record tap only — select on click (pointerdown+click
              // used to toggle open→closed on a single tap).
              if (!canEdit) {
                tapStartRef.current = { id: o.id, x: e.clientX, y: e.clientY };
                return;
              }
              onSelect?.(o.id);
              if (!dragOk) return;
              (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
              onMoveStart?.(o.id, e.clientX, e.clientY);
            }}
            onClick={(e) => {
              if (!canTap) return;
              e.stopPropagation();
              if (!canEdit) {
                const start = tapStartRef.current;
                tapStartRef.current = null;
                if (
                  start &&
                  start.id === o.id &&
                  Math.hypot(e.clientX - start.x, e.clientY - start.y) > 14
                ) {
                  return;
                }
              }
              onSelect?.(o.id);
            }}
            className={`absolute touch-none select-none overflow-hidden ${
              canEdit
                ? locked
                  ? "cursor-default"
                  : "cursor-grab active:cursor-grabbing"
                : canTap
                  ? "cursor-pointer"
                  : "pointer-events-none"
            }`}
            style={{
              left: `${o.x}%`,
              top: `${o.y}%`,
              width: `${o.w}%`,
              height: `${o.h}%`,
              zIndex: selected ? 45 : isEventLink ? 28 : isBooth ? 18 : 15,
              borderRadius: isEventLink ? 999 : 6,
              borderStyle: "solid",
              borderWidth: selected ? Math.max(borderPx, 2 * inv) : borderPx,
              borderColor: selected
                ? selectedStroke
                : locked
                  ? "#94a3b8"
                  : stroke,
              backgroundColor: selected
                ? selectedFill
                : locked
                  ? "rgba(148,163,184,0.35)"
                  : isBooth
                    ? `${fill}aa`
                    : isEventLink && accent
                      ? hexAlpha(accent, "E6")
                      : `${fill}cc`,
              boxShadow: selected ? selGlow : isEventLink ? "0 1px 4px rgba(15,23,42,0.18)" : "none",
              opacity: o.showInApp === false ? 0.55 : 1,
              fontFamily: "system-ui, Segoe UI, sans-serif",
              color: isEventLink && accent ? "#fff" : undefined,
            }}
            title={
              canEdit
                ? `${title}${locked ? " · закреплён" : " — тяните углы для размера"}`
                : o.showInApp === false
                  ? `${title} · скрыт в приложении`
                  : title
            }
          >
            {isBooth ? (
              <PinCardLabels
                boothCode={codeText}
                names={names.length ? names : label ? [label] : []}
                boxWPct={o.w}
                boxHPct={o.h}
                boardW={boardWidth}
                boardH={boardHeight}
                boardRotation={boardRotation}
                nameRotation={nameRot}
                nameLines={3}
                labelScale={baseScale * objScale}
              />
            ) : (
              (() => {
                const { textPx, pad } = fitPinLabel({
                  wPct: o.w,
                  hPct: o.h,
                  boardW: boardWidth,
                  boardH: boardHeight,
                  labelScale: baseScale * objScale,
                  nameLines: 3,
                  nameRot: nameRot,
                  nameText: label,
                });
                const swapped = nameRot === 90 || nameRot === 270;
                return (
                  <span className="absolute inset-0 overflow-hidden pointer-events-none">
                    <span
                      className="absolute flex items-center justify-center text-center"
                      style={{
                        left: "50%",
                        top: "50%",
                        width: swapped ? `${(o.h / o.w) * 100}%` : "100%",
                        height: swapped ? `${(o.w / o.h) * 100}%` : "100%",
                        transform: `translate(-50%, -50%)${
                          nameRot ? ` rotate(${nameRot}deg)` : ""
                        }`,
                        padding: pad,
                        boxSizing: "border-box",
                      }}
                    >
                      <span
                        className={`leading-tight w-full ${
                          o.kind === "event_link"
                            ? "flex items-center justify-center gap-0.5"
                            : ""
                        }`}
                        style={{
                          color:
                            isEventLink && accent ? "#ffffff" : textCol,
                          fontSize: textPx,
                          fontWeight: isEventLink ? 800 : 600,
                          ...(isEventLink
                            ? {}
                            : {
                                display: "-webkit-box",
                                WebkitLineClamp: 4,
                                WebkitBoxOrient: "vertical" as const,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                overflowWrap: "anywhere",
                                wordBreak: "break-word",
                              }),
                        }}
                      >
                        {isEventLink &&
                        (o.linkArrow === "w" || o.linkArrow === "n") ? (
                          <LinkArrowIcon
                            dir={o.linkArrow}
                            size={Math.max(11, textPx + 1)}
                            color={accent ? "#ffffff" : stroke}
                          />
                        ) : null}
                        <span
                          className={
                            isEventLink
                              ? "min-w-0 line-clamp-3 text-left font-extrabold"
                              : undefined
                          }
                          style={
                            isEventLink
                              ? { fontWeight: 800, letterSpacing: "-0.01em" }
                              : undefined
                          }
                        >
                          {label}
                        </span>
                        {isEventLink &&
                        (o.linkArrow !== "w" && o.linkArrow !== "n") ? (
                          <LinkArrowIcon
                            dir={o.linkArrow ?? "e"}
                            size={Math.max(10, textPx)}
                            color={accent ? "#ffffff" : stroke}
                          />
                        ) : null}
                      </span>
                    </span>
                  </span>
                );
              })()
            )}

            {locked && canEdit ? (
              <span
                className="absolute z-30 w-3.5 h-3.5 rounded-full bg-slate-700 text-white flex items-center justify-center shadow-sm pointer-events-none"
                style={{ top: 0, right: 0, transform: "translate(40%, -40%)" }}
                aria-hidden
              >
                <Lock size={8} strokeWidth={2.5} />
              </span>
            ) : null}

            {canEdit && selected && !locked
              ? (["nw", "ne", "sw", "se"] as ResizeCorner[]).map((corner) => {
                  const pos =
                    corner === "nw"
                      ? { left: -handlePx / 2, top: -handlePx / 2 }
                      : corner === "ne"
                        ? { right: -handlePx / 2, top: -handlePx / 2 }
                        : corner === "sw"
                          ? { left: -handlePx / 2, bottom: -handlePx / 2 }
                          : { right: -handlePx / 2, bottom: -handlePx / 2 };
                  return (
                    <span
                      key={corner}
                      data-map-no-pan=""
                      data-resize-handle=""
                      onPointerDown={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
                        onSelect?.(o.id);
                        onResizeStart?.(o.id, corner, e.clientX, e.clientY);
                      }}
                      className="absolute z-50 rounded-sm bg-indigo-600 border-2 border-white shadow-md"
                      style={{
                        ...pos,
                        width: handlePx,
                        height: handlePx,
                        cursor: cursorForCorner(corner, boardRotation),
                      }}
                      title="Изменить размер"
                    />
                  );
                })
              : null}
          </div>
        );
      })}
    </>
  );
}

export type { ResizeCorner as MarkerResizeCorner };
